import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnSizingState } from '@tanstack/react-table';
import { LocalStorageAcceptableKeys } from '@/common/types';

type UseTanTableColumnSizingProps = {
  localStorageKey?: LocalStorageAcceptableKeys;
  columnSizing?: ColumnSizingState;
  setColumnSizing?: (newColumnSizing: ColumnSizingState) => void;
};

export const useTanTableColumnSizing = ({
  localStorageKey,
  columnSizing,
  setColumnSizing,
}: UseTanTableColumnSizingProps) => {
  const [internalColumnSizing, setInternalColumnSizing] =
    useState<ColumnSizingState>({});

  const columnSizingState =
    columnSizing !== undefined ? columnSizing : internalColumnSizing;

  const columnWidthsStorageKey = useMemo(
    () => (localStorageKey ? `columnWidths_${localStorageKey}` : undefined),
    [localStorageKey],
  );

  useEffect(() => {
    if (!columnWidthsStorageKey) return;

    try {
      const stored = localStorage.getItem(columnWidthsStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnSizingState;
        setInternalColumnSizing(parsed);
        if (setColumnSizing) {
          setColumnSizing(parsed);
        }
      }
    } catch {
      // ignore malformed localStorage values
    }
  }, [columnWidthsStorageKey]);

  const handleColumnSizingChange = useCallback(
    (
      updater:
        | ColumnSizingState
        | ((old: ColumnSizingState) => ColumnSizingState),
    ) => {
      // If external sizing state is provided, treat it as the single source
      // of truth and base updates on it instead of the internal state.
      if (columnSizing !== undefined && setColumnSizing) {
        const next =
          typeof updater === 'function'
            ? (updater as (old: ColumnSizingState) => ColumnSizingState)(
                columnSizing,
              )
            : updater;

        setColumnSizing(next);
        setInternalColumnSizing(next);

        if (columnWidthsStorageKey) {
          try {
            localStorage.setItem(columnWidthsStorageKey, JSON.stringify(next));
          } catch {
            // ignore storage errors (e.g. quota exceeded)
          }
        }

        return;
      }

      // Fallback: manage column sizing locally when no external state is given.
      setInternalColumnSizing((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (old: ColumnSizingState) => ColumnSizingState)(prev)
            : updater;

        if (columnWidthsStorageKey) {
          try {
            localStorage.setItem(columnWidthsStorageKey, JSON.stringify(next));
          } catch {
            // ignore storage errors (e.g. quota exceeded)
          }
        }

        return next;
      });
    },
    [columnWidthsStorageKey, columnSizing, setColumnSizing],
  );

  return {
    columnSizingState,
    handleColumnSizingChange,
  };
};
